/**
 * Canonical blog metadata catalog shared by frontend routes and server consumers.
 *
 * What: Exports validated, frozen blog post entries sourced from MDX frontmatter.
 * Why: Keeps blog routing, sitemap generation, and content lookup aligned to one source of truth.
 * When: The underlying data is auto-generated by scripts/generate-blog-catalog-data.js
 *       from YAML frontmatter in src/content/blog/posts/*.mdx.
 */

import {
  RAW_BLOG_POST_ENTRIES,
  type RawBlogPostEntry,
} from './_generatedBlogPostEntries.js';

export type { RawBlogPostEntry };

export const BLOG_POST_ROUTE_BASE_PATH = '/blog';
export const BLOG_POST_CONTENT_DIRECTORY = 'src/content/blog/posts';

const BLOG_POST_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export type BlogPostRoutePath = `/blog/${string}`;

export interface BlogPostCatalogEntry
  extends Readonly<Omit<RawBlogPostEntry, 'tags' | 'expertiseAreas'>> {
  readonly routePath: BlogPostRoutePath;
  readonly tags: readonly string[];
  readonly expertiseAreas: readonly string[];
}

const normalizedCatalogEntries: BlogPostCatalogEntry[] =
  RAW_BLOG_POST_ENTRIES.map((entry: RawBlogPostEntry) => {
    if (!BLOG_POST_SLUG_PATTERN.test(entry.slug)) {
      throw new Error(`Invalid blog slug format: "${entry.slug}".`);
    }

    if (!entry.contentFileName.endsWith('.mdx')) {
      throw new Error(
        `Blog post "${entry.slug}" must map to an .mdx file, got "${entry.contentFileName}".`
      );
    }

    return Object.freeze({
      ...entry,
      routePath: `${BLOG_POST_ROUTE_BASE_PATH}/${entry.slug}`,
      tags: Object.freeze([...entry.tags]),
      expertiseAreas: Object.freeze([...entry.expertiseAreas]),
    }) as BlogPostCatalogEntry;
  });

assertUniqueBlogField(normalizedCatalogEntries, 'slug');
assertUniqueBlogField(normalizedCatalogEntries, 'contentFileName');

export const BLOG_POST_CATALOG: readonly BlogPostCatalogEntry[] = Object.freeze(
  normalizedCatalogEntries
);

export const BLOG_POST_CATALOG_BY_SLUG: ReadonlyMap<
  string,
  BlogPostCatalogEntry
> = new Map(BLOG_POST_CATALOG.map(entry => [entry.slug, entry]));

/**
 * Normalize a blog slug from route-like input into canonical slug format.
 *
 * @param rawSlug - Raw slug or route fragment (e.g. "/blog/example/").
 * @returns Normalized slug key, or empty string when input is invalid.
 */
export function normalizeBlogSlug(rawSlug: string): string {
  if (typeof rawSlug !== 'string') {
    return '';
  }

  const trimmedSlug = rawSlug.trim().toLowerCase();
  if (!trimmedSlug) {
    return '';
  }

  return trimmedSlug
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .replace(/^blog\//, '');
}

/**
 * Return an array snapshot of all catalog entries.
 *
 * @returns Catalog entries in declaration order.
 */
export function listBlogPostCatalogEntries(): BlogPostCatalogEntry[] {
  return BLOG_POST_CATALOG.slice();
}

/**
 * Resolve one blog catalog entry by slug.
 *
 * @param rawSlug - Slug candidate from URL params or links.
 * @returns Catalog entry when found; otherwise null.
 */
export function getBlogPostCatalogEntryBySlug(
  rawSlug: string
): BlogPostCatalogEntry | null {
  const normalizedSlug = normalizeBlogSlug(rawSlug);
  if (!normalizedSlug) {
    return null;
  }

  return BLOG_POST_CATALOG_BY_SLUG.get(normalizedSlug) ?? null;
}

function assertUniqueBlogField(
  entries: readonly BlogPostCatalogEntry[],
  fieldName: keyof BlogPostCatalogEntry
): void {
  const valueSet = new Set<unknown>();

  entries.forEach(entry => {
    const fieldValue = entry[fieldName];
    if (valueSet.has(fieldValue)) {
      throw new Error(
        `Duplicate blog catalog ${String(fieldName)}: "${String(fieldValue)}".`
      );
    }

    valueSet.add(fieldValue);
  });
}
